import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  CheckCircle2,
  Clock,
  Gift,
  ImagePlus,
  MapPin,
  ShieldCheck,
  Star,
  Trash2,
  User,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { SectionHeading } from "@/components/section-heading";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  FORM_TEXT_MAX_LENGTH,
  FORM_TEXT_MIN_LENGTH,
  FormTextCounter,
} from "@/components/form-text-counter";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLanguage } from "@/lib/i18n";
import { toast } from "sonner";
import { useServerFn } from "@tanstack/react-start";
import { submitForm } from "@/lib/forms.functions";
import { emailMessages } from "@/lib/email/messages";
import { cleanupJobImages, uploadJobImages } from "@/lib/uploads.functions";
import { filesToPayload } from "@/lib/file-upload";
import { getAuthStatus } from "@/lib/auth.functions";
import { isCustomJobBudget, normalizeJobBudget } from "@/lib/job-budget";
import {
  jobBudgetLabel,
  jobCategoryLabel,
  jobObjectLabel,
  jobTimelineLabel,
} from "@/lib/jobs.types";

export const Route = createFileRoute("/auftrag-einstellen")({
  staticData: { sitemap: true, toasts: true },
  loader: () => getAuthStatus(),
  head: () => ({
    meta: [
      { title: "Auftrag kostenlos einstellen. Begner" },
      {
        name: "description",
        content:
          "Auftrag mit Bildern kostenlos einreichen, prüfen lassen und 30 Tage für geprüfte Auftragnehmer veröffentlichen.",
      },
      { property: "og:title", content: "Auftrag kostenlos einstellen. Begner" },
      {
        property: "og:description",
        content: "Projekt beschreiben, Bilder ergänzen und nach Prüfung kostenlos veröffentlichen.",
      },
    ],
  }),
  component: AuftragPage,
});

const stepIcons = [ClipboardList, ClipboardList, MapPin, User] as const;

const MAX_JOB_IMAGES = 8;
const MAX_IMAGE_BYTES = 6 * 1024 * 1024;
const JOB_UPLOAD_CONCURRENCY = 2;
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

const accountGateCopy = {
  de: {
    eyebrow: "Kostenloses Kundenkonto",
    title: "Melden Sie sich an, bevor Sie Ihren Auftrag erstellen.",
    text: "So bleibt Ihr Entwurf sicher Ihrem Konto zugeordnet und Sie sehen Status, Inserat und Feedback später unter «Mein Begner-Konto».",
    login: "Anmelden",
    register: "Kundenkonto erstellen",
  },
  fr: {
    eyebrow: "Compte client gratuit",
    title: "Connectez-vous avant de créer votre mandat.",
    text: "Votre demande reste ainsi liée à votre compte et vous retrouvez son statut, l’annonce et vos commentaires dans « Mon compte Begner ».",
    login: "Se connecter",
    register: "Créer un compte client",
  },
  it: {
    eyebrow: "Account cliente gratuito",
    title: "Acceda prima di creare il suo incarico.",
    text: "In questo modo l’incarico resta collegato al suo account e potrà vedere stato, annuncio e feedback in « Il mio account Begner ».",
    login: "Accedi",
    register: "Crea account cliente",
  },
  en: {
    eyebrow: "Free customer account",
    title: "Sign in before creating your job.",
    text: "This securely links the request to your account, where you can later see its status, listing, and feedback.",
    login: "Sign in",
    register: "Create customer account",
  },
} as const;

type SelectedImage = {
  id: string;
  file: File;
  preview: string;
};

const jobCopy = {
  de: {
    validationError: "Bitte füllen Sie alle Pflichtfelder aus.",
    successToast: "Auftrag erfolgreich zur Prüfung eingereicht!",
    eyebrow: "Anfrage stellen",
    title: "Auftrag kostenlos einstellen",
    intro:
      "Erstellen Sie Ihren Auftrag in 4 kurzen Schritten. Nach unserer Prüfung veröffentlichen wir ihn kostenlos für 30 Tage.",
    requiredHint: "* Pflichtfelder",
    steps: ["Leistung", "Details", "Ort", "Kontakt"],
    back: "Zurück",
    next: "Weiter",
    submit: "Anfrage absenden",
    sideTitle: "Warum Begner?",
    benefits: [
      "Veröffentlichung erst nach persönlicher Prüfung",
      "Sie wählen bis zu 5 Firmen für die Kontaktfreigabe",
      "30 Tage kostenlos und unverbindlich online",
    ],
    tipTitle: "Tipp",
    tipText:
      "Je präziser Ihre Beschreibung, desto schneller und genauer erhalten Sie passende Offerten.",
    categories: [
      { id: "kernbohrung", label: "Kernbohrung" },
      { id: "reinigung", label: "Reinigung" },
      { id: "umzug", label: "Umzug" },
      { id: "sanitaer", label: "Sanitär & Heizung" },
      { id: "elektriker", label: "Elektrik & Smart Home" },
      { id: "maler", label: "Malerei & Trockenbau" },
      { id: "dach", label: "Dach & Spengler" },
      { id: "boden", label: "Boden & Platten" },
      { id: "fenster", label: "Fenster & Türen" },
      { id: "renovation", label: "Renovation & Umbau" },
      { id: "bau", label: "Bau & Abbruch" },
      { id: "garten", label: "Garten & Umgebung" },
      { id: "kueche-schreinerei", label: "Küche & Schreinerei" },
      { id: "energie", label: "Photovoltaik & Energie" },
      { id: "planung", label: "Planung & Bauleitung" },
      { id: "sonstiges", label: "Weitere Dienstleistungen" },
    ],
    step1Title: "Welche Leistung benötigen Sie?",
    step1Text: "Wählen Sie eine Kategorie. Sie können später noch Details ergänzen.",
    titleLabel: "Kurzer Titel Ihres Projekts",
    titlePlaceholder: "z. B. Umzugsreinigung für 4-Zimmer-Wohnung",
    step2Title: "Beschreiben Sie Ihr Projekt",
    descriptionLabel: "Detaillierte Beschreibung",
    descriptionPlaceholder: "Umfang, Räume, Materialien, besondere Anforderungen…",
    publicContentHint:
      "Beschreibung und Bilder sind öffentlich. Bitte keine Telefonnummern, E-Mail-Adressen, vollständigen Namen oder genaue Privatadressen darin nennen.",
    charactersRemaining: "Zeichen",
    descriptionMinimum: "mindestens 40 Zeichen",
    timelineLabel: "Zeitrahmen",
    budgetLabel: "Budget",
    customBudget: "Eigenen Betrag eingeben",
    customBudgetAmount: "Ihr Budget in CHF",
    customBudgetHint: "Bitte einen Betrag grösser als 0 mit höchstens 2 Nachkommastellen eingeben.",
    imageTitle: "Bilder zum Auftrag",
    imageText:
      "Zeigen Sie den aktuellen Zustand oder wichtige Details. Gute Bilder helfen Auftragnehmern, den Aufwand besser einzuschätzen.",
    imageAdd: "Bilder auswählen",
    imageOptional: "Optional · bis zu 8 Bilder · JPG, PNG oder WebP · max. 6 MB pro Bild",
    imageDrop: "Hierher ziehen oder vom Gerät auswählen",
    imageCount: "Bilder ausgewählt",
    imageCover: "Titelbild",
    imageMakeCover: "Als Titelbild festlegen",
    imageMoveEarlier: "Bild nach vorne verschieben",
    imageMoveLater: "Bild nach hinten verschieben",
    imageRemove: "Bild entfernen",
    imageTooMany: "Sie können höchstens 8 Bilder hochladen.",
    imageInvalidType: "Bitte verwenden Sie nur JPG-, PNG- oder WebP-Bilder.",
    imageTooLarge: "Ein Bild ist grösser als 6 MB.",
    imageUploadFailed:
      "Die Bilder konnten nicht hochgeladen werden. Bitte versuchen Sie es erneut oder senden Sie den Auftrag ohne Bilder.",
    timelines: [
      { value: "asap", label: "So schnell wie möglich" },
      { value: "1-3m", label: "In 1 bis 3 Monaten" },
      { value: "3-6m", label: "In 3 bis 6 Monaten" },
      { value: "flex", label: "Flexibel" },
    ],
    budgets: [
      { value: "<5k", label: "Bis 5'000 CHF" },
      { value: "5-20k", label: "5'000 bis 20'000 CHF" },
      { value: "20-50k", label: "20'000 bis 50'000 CHF" },
      { value: ">50k", label: "Über 50'000 CHF" },
      { value: "unknown", label: "Noch unklar" },
    ],
    step3Title: "Wo soll die Arbeit ausgeführt werden?",
    step3Text: "Wir vermitteln Auftragnehmer aus Ihrer Region.",
    postcode: "PLZ",
    city: "Ort",
    object: "Objekt",
    objectPlaceholder: "Objekttyp auswählen",
    objects: [
      { value: "efh", label: "Einfamilienhaus" },
      { value: "mfh", label: "Mehrfamilienhaus" },
      { value: "whg", label: "Wohnung" },
      { value: "gewerbe", label: "Gewerbeobjekt" },
      { value: "other", label: "Anderes" },
    ],
    step4Title: "Wie sollen die Auftragnehmer Sie erreichen?",
    step4Text:
      "Nach unserer Prüfung wird Ihr Auftrag öffentlich. Geprüfte Firmen mit Pro-Zugang können 72 Stunden lang Interesse bekunden. Danach wählen Sie innerhalb von 7 Tagen bis zu 5 Firmen aus. Nur diese dürfen Ihre Kontaktdaten ansehen und Sie zu diesem Auftrag kontaktieren. Eine Kontaktfreigabe ist noch keine Auftragsvergabe.",
    firstName: "Vorname",
    lastName: "Nachname",
    email: "E-Mail",
    phone: "Telefon (optional)",
    agreePrefix: "Ich akzeptiere die",
    terms: "AGB",
    agreeMiddle: "und die",
    privacy: "Datenschutzerklärung",
    successTitle: "Auftrag zur Prüfung eingereicht!",
    successIntro: "Vielen Dank",
    successTextBeforeTitle: "Ihr Auftrag",
    successTextAfterTitle: "für",
    successTextEnd:
      "ist gespeichert und wird nun von uns geprüft. Den aktuellen Stand finden Sie jederzeit in Ihrem Begner-Konto.",
    successEmailMissing:
      "Der Auftrag ist gespeichert, aber die Bestätigungs-E-Mail konnte nicht versendet werden. Bitte kontaktieren Sie info@begner.ch und nennen Sie Ihre Referenz.",
    discover: "Auftragnehmer entdecken",
    home: "Zur Startseite",
  },
  fr: {
    validationError: "Veuillez remplir tous les champs obligatoires.",
    successToast: "Mandat soumis avec succès pour vérification!",
    eyebrow: "Déposer une demande",
    title: "Publier un mandat gratuitement",
    intro:
      "Créez votre mandat en 4 étapes rapides. Après vérification, nous le publions gratuitement pendant 30 jours.",
    requiredHint: "* Champs obligatoires",
    steps: ["Prestation", "Détails", "Lieu", "Contact"],
    back: "Retour",
    next: "Suivant",
    submit: "Envoyer la demande",
    sideTitle: "Pourquoi Begner?",
    benefits: [
      "Publication uniquement après vérification personnelle",
      "Vous choisissez jusqu’à 5 entreprises pour partager vos coordonnées",
      "En ligne 30 jours, gratuitement et sans engagement",
    ],
    tipTitle: "Conseil",
    tipText:
      "Plus votre description est précise, plus les offres adaptées arrivent vite et avec exactitude.",
    categories: [
      { id: "kernbohrung", label: "Carottage" },
      { id: "reinigung", label: "Nettoyage" },
      { id: "umzug", label: "Déménagement" },
      { id: "sanitaer", label: "Sanitaire & chauffage" },
      { id: "elektriker", label: "Électricité & domotique" },
      { id: "maler", label: "Peinture & plâtrerie" },
      { id: "dach", label: "Toiture & ferblanterie" },
      { id: "boden", label: "Sols & carrelage" },
      { id: "fenster", label: "Fenêtres & portes" },
      { id: "renovation", label: "Rénovation & transformation" },
      { id: "bau", label: "Construction & démolition" },
      { id: "garten", label: "Jardin & aménagements extérieurs" },
      { id: "kueche-schreinerei", label: "Cuisine & menuiserie" },
      { id: "energie", label: "Photovoltaïque & énergie" },
      { id: "planung", label: "Planification & direction des travaux" },
      { id: "sonstiges", label: "Autres prestations" },
    ],
    step1Title: "De quelle prestation avez-vous besoin?",
    step1Text: "Choisissez une catégorie. Vous pourrez ajouter des détails plus tard.",
    titleLabel: "Titre court de votre projet",
    titlePlaceholder: "p. ex. nettoyage de déménagement pour 4 pièces",
    step2Title: "Décrivez votre projet",
    descriptionLabel: "Description détaillée",
    descriptionPlaceholder: "Volume, pièces, matériaux, exigences particulières…",
    publicContentHint:
      "La description et les images sont publiques. N’y indiquez pas de téléphone, d’adresse e-mail, de nom complet ou d’adresse privée précise.",
    charactersRemaining: "caractères",
    descriptionMinimum: "au moins 40 caractères",
    timelineLabel: "Calendrier",
    budgetLabel: "Budget",
    customBudget: "Saisir un montant",
    customBudgetAmount: "Votre budget en CHF",
    customBudgetHint: "Saisissez un montant supérieur à 0 avec au maximum 2 décimales.",
    imageTitle: "Photos du mandat",
    imageText:
      "Montrez l’état actuel ou les détails importants. De bonnes photos aident les prestataires à mieux estimer le travail.",
    imageAdd: "Choisir des photos",
    imageOptional: "Optionnel · jusqu’à 8 photos · JPG, PNG ou WebP · max. 6 Mo par photo",
    imageDrop: "Déposer ici ou sélectionner sur l’appareil",
    imageCount: "photos sélectionnées",
    imageCover: "Photo principale",
    imageMakeCover: "Définir comme photo principale",
    imageMoveEarlier: "Déplacer la photo vers l’avant",
    imageMoveLater: "Déplacer la photo vers l’arrière",
    imageRemove: "Supprimer la photo",
    imageTooMany: "Vous pouvez télécharger au maximum 8 photos.",
    imageInvalidType: "Veuillez utiliser uniquement des images JPG, PNG ou WebP.",
    imageTooLarge: "Une image dépasse 6 Mo.",
    imageUploadFailed:
      "Les photos n’ont pas pu être téléchargées. Veuillez réessayer ou envoyer le mandat sans photos.",
    timelines: [
      { value: "asap", label: "Dès que possible" },
      { value: "1-3m", label: "Dans 1 à 3 mois" },
      { value: "3-6m", label: "Dans 3 à 6 mois" },
      { value: "flex", label: "Flexible" },
    ],
    budgets: [
      { value: "<5k", label: "Jusqu'à 5'000 CHF" },
      { value: "5-20k", label: "5'000 à 20'000 CHF" },
      { value: "20-50k", label: "20'000 à 50'000 CHF" },
      { value: ">50k", label: "Plus de 50'000 CHF" },
      { value: "unknown", label: "Pas encore clair" },
    ],
    step3Title: "Où les travaux doivent-ils être réalisés?",
    step3Text: "Nous vous mettons en relation avec des prestataires de votre région.",
    postcode: "NPA",
    city: "Localité",
    object: "Objet",
    objectPlaceholder: "Sélectionner le type d’objet",
    objects: [
      { value: "efh", label: "Maison individuelle" },
      { value: "mfh", label: "Immeuble collectif" },
      { value: "whg", label: "Appartement" },
      { value: "gewerbe", label: "Objet commercial" },
      { value: "other", label: "Autre" },
    ],
    step4Title: "Comment les prestataires peuvent-ils vous joindre?",
    step4Text:
      "Après vérification, votre mandat est publié. Les entreprises vérifiées avec un accès Pro ont 72 heures pour manifester leur intérêt. Vous avez ensuite 7 jours pour en sélectionner jusqu’à 5. Seules ces entreprises peuvent consulter vos coordonnées et vous contacter pour ce mandat. Cette autorisation n’attribue pas le mandat.",
    firstName: "Prénom",
    lastName: "Nom",
    email: "E-mail",
    phone: "Téléphone (optionnel)",
    agreePrefix: "J'accepte les",
    terms: "conditions générales",
    agreeMiddle: "et la",
    privacy: "déclaration de confidentialité",
    successTitle: "Mandat soumis pour vérification!",
    successIntro: "Merci",
    successTextBeforeTitle: "Votre mandat",
    successTextAfterTitle: "pour",
    successTextEnd:
      "est enregistré et va maintenant être vérifié. Vous retrouverez son état à tout moment dans votre compte Begner.",
    successEmailMissing:
      "Le mandat est enregistré, mais l’e-mail de confirmation n’a pas pu être envoyé. Contactez info@begner.ch en indiquant votre référence.",
    discover: "Découvrir les prestataires",
    home: "Retour à l'accueil",
  },
  it: {
    validationError: "Compili tutti i campi obbligatori.",
    successToast: "Incarico inviato con successo per la verifica!",
    eyebrow: "Inviare richiesta",
    title: "Pubblica un incarico gratuitamente",
    intro:
      "Crei il suo incarico in 4 brevi passi. Dopo la verifica lo pubblichiamo gratuitamente per 30 giorni.",
    requiredHint: "* Campi obbligatori",
    steps: ["Prestazione", "Dettagli", "Luogo", "Contatto"],
    back: "Indietro",
    next: "Avanti",
    submit: "Invia richiesta",
    sideTitle: "Perché Begner?",
    benefits: [
      "Pubblicazione solo dopo una verifica personale",
      "Seleziona fino a 5 imprese autorizzate a vedere i contatti",
      "Online per 30 giorni, gratis e senza impegno",
    ],
    tipTitle: "Consiglio",
    tipText: "Più precisa è la descrizione, più rapidamente riceverà offerte adatte e accurate.",
    categories: [
      { id: "kernbohrung", label: "Carotaggio" },
      { id: "reinigung", label: "Pulizia" },
      { id: "umzug", label: "Trasloco" },
      { id: "sanitaer", label: "Sanitari & riscaldamento" },
      { id: "elektriker", label: "Impianti elettrici & domotica" },
      { id: "maler", label: "Pittura & cartongesso" },
      { id: "dach", label: "Tetti & lattoneria" },
      { id: "boden", label: "Pavimenti & piastrelle" },
      { id: "fenster", label: "Finestre & porte" },
      { id: "renovation", label: "Ristrutturazione & trasformazione" },
      { id: "bau", label: "Costruzione & demolizione" },
      { id: "garten", label: "Giardino & sistemazioni esterne" },
      { id: "kueche-schreinerei", label: "Cucine & falegnameria" },
      { id: "energie", label: "Fotovoltaico & energia" },
      { id: "planung", label: "Progettazione & direzione lavori" },
      { id: "sonstiges", label: "Altri servizi" },
    ],
    step1Title: "Di quale prestazione ha bisogno?",
    step1Text: "Scelga una categoria. Potrà aggiungere dettagli in seguito.",
    titleLabel: "Titolo breve del progetto",
    titlePlaceholder: "es. pulizia trasloco per appartamento di 4 locali",
    step2Title: "Descriva il suo progetto",
    descriptionLabel: "Descrizione dettagliata",
    descriptionPlaceholder: "Entità, locali, materiali, requisiti particolari…",
    publicContentHint:
      "Descrizione e immagini sono pubbliche. Non inserisca numeri di telefono, e-mail, nomi completi o indirizzi privati precisi.",
    charactersRemaining: "caratteri",
    descriptionMinimum: "almeno 40 caratteri",
    timelineLabel: "Tempistica",
    budgetLabel: "Budget",
    customBudget: "Inserire un importo",
    customBudgetAmount: "Il suo budget in CHF",
    customBudgetHint: "Inserisca un importo maggiore di 0 con al massimo 2 decimali.",
    imageTitle: "Immagini dell’incarico",
    imageText:
      "Mostri lo stato attuale o i dettagli importanti. Buone immagini aiutano le imprese a stimare meglio il lavoro.",
    imageAdd: "Seleziona immagini",
    imageOptional: "Opzionale · fino a 8 immagini · JPG, PNG o WebP · max. 6 MB per immagine",
    imageDrop: "Trascina qui o seleziona dal dispositivo",
    imageCount: "immagini selezionate",
    imageCover: "Immagine principale",
    imageMakeCover: "Imposta come immagine principale",
    imageMoveEarlier: "Sposta l’immagine indietro",
    imageMoveLater: "Sposta l’immagine avanti",
    imageRemove: "Rimuovi immagine",
    imageTooMany: "È possibile caricare al massimo 8 immagini.",
    imageInvalidType: "Utilizzi solo immagini JPG, PNG o WebP.",
    imageTooLarge: "Un’immagine supera i 6 MB.",
    imageUploadFailed:
      "Non è stato possibile caricare le immagini. Riprovi oppure invii l’incarico senza immagini.",
    timelines: [
      { value: "asap", label: "Il prima possibile" },
      { value: "1-3m", label: "Tra 1 e 3 mesi" },
      { value: "3-6m", label: "Tra 3 e 6 mesi" },
      { value: "flex", label: "Flessibile" },
    ],
    budgets: [
      { value: "<5k", label: "Fino a 5'000 CHF" },
      { value: "5-20k", label: "5'000 a 20'000 CHF" },
      { value: "20-50k", label: "20'000 a 50'000 CHF" },
      { value: ">50k", label: "Oltre 50'000 CHF" },
      { value: "unknown", label: "Ancora da definire" },
    ],
    step3Title: "Dove devono essere eseguiti i lavori?",
    step3Text: "Le segnaliamo imprese della sua regione.",
    postcode: "NPA",
    city: "Luogo",
    object: "Oggetto",
    objectPlaceholder: "Seleziona il tipo di oggetto",
    objects: [
      { value: "efh", label: "Casa unifamiliare" },
      { value: "mfh", label: "Casa plurifamiliare" },
      { value: "whg", label: "Appartamento" },
      { value: "gewerbe", label: "Oggetto commerciale" },
      { value: "other", label: "Altro" },
    ],
    step4Title: "Come possono raggiungerla le imprese?",
    step4Text:
      "Dopo la verifica pubblichiamo il suo incarico. Le imprese verificate con accesso Pro hanno 72 ore per manifestare interesse. Poi ha 7 giorni per selezionarne fino a 5. Solo queste possono vedere i contatti e contattarla per questo incarico. La condivisione non assegna ancora l’incarico.",
    firstName: "Nome",
    lastName: "Cognome",
    email: "E-mail",
    phone: "Telefono (opzionale)",
    agreePrefix: "Accetto le",
    terms: "CG",
    agreeMiddle: "e la",
    privacy: "dichiarazione sulla protezione dei dati",
    successTitle: "Incarico inviato per la verifica!",
    successIntro: "Grazie",
    successTextBeforeTitle: "Il suo incarico",
    successTextAfterTitle: "per",
    successTextEnd:
      "è stato salvato e sarà ora verificato. Può controllarne lo stato in qualsiasi momento nel suo account Begner.",
    successEmailMissing:
      "L’incarico è stato salvato, ma non è stato possibile inviare l’e-mail di conferma. Contatti info@begner.ch indicando il riferimento.",
    discover: "Scopri imprese",
    home: "Alla pagina iniziale",
  },
  en: {
    validationError: "Please complete all required fields.",
    successToast: "Job successfully submitted for review!",
    eyebrow: "Submit a request",
    title: "Post a job for free",
    intro:
      "Create your job in 4 short steps. After review, we publish it free of charge for 30 days.",
    requiredHint: "* Required fields",
    steps: ["Service", "Details", "Location", "Contact"],
    back: "Back",
    next: "Continue",
    submit: "Submit request",
    sideTitle: "Why Begner?",
    benefits: [
      "Published only after personal review",
      "You choose up to 5 companies to receive contact access",
      "Online for 30 days, free and without obligation",
    ],
    tipTitle: "Tip",
    tipText:
      "The more precise your description, the sooner you will receive accurate, suitable quotes.",
    categories: [
      { id: "kernbohrung", label: "Core drilling" },
      { id: "reinigung", label: "Cleaning" },
      { id: "umzug", label: "Moving" },
      { id: "sanitaer", label: "Plumbing & heating" },
      { id: "elektriker", label: "Electrical & smart home" },
      { id: "maler", label: "Painting & drywall" },
      { id: "dach", label: "Roofing & sheet metal" },
      { id: "boden", label: "Flooring & tiling" },
      { id: "fenster", label: "Windows & doors" },
      { id: "renovation", label: "Renovation & conversion" },
      { id: "bau", label: "Construction & demolition" },
      { id: "garten", label: "Garden & outdoor areas" },
      { id: "kueche-schreinerei", label: "Kitchens & carpentry" },
      { id: "energie", label: "Solar & energy" },
      { id: "planung", label: "Planning & site management" },
      { id: "sonstiges", label: "Other services" },
    ],
    step1Title: "Which service do you need?",
    step1Text: "Choose a category. You can add details later.",
    titleLabel: "Short project title",
    titlePlaceholder: "e.g. end-of-tenancy cleaning for a 4-room apartment",
    step2Title: "Describe your project",
    descriptionLabel: "Detailed description",
    descriptionPlaceholder: "Scope, rooms, materials, special requirements…",
    publicContentHint:
      "Descriptions and images are public. Please leave out phone numbers, email addresses, full names and exact private addresses.",
    charactersRemaining: "characters",
    descriptionMinimum: "at least 40 characters",
    timelineLabel: "Timeline",
    budgetLabel: "Budget",
    customBudget: "Enter your own amount",
    customBudgetAmount: "Your budget in CHF",
    customBudgetHint: "Enter an amount greater than 0 with no more than 2 decimal places.",
    imageTitle: "Job images",
    imageText:
      "Show the current condition or important details. Good images help contractors estimate the work more accurately.",
    imageAdd: "Select images",
    imageOptional: "Optional · up to 8 images · JPG, PNG or WebP · max. 6 MB per image",
    imageDrop: "Drag here or select from your device",
    imageCount: "images selected",
    imageCover: "Cover image",
    imageMakeCover: "Set as cover image",
    imageMoveEarlier: "Move image earlier",
    imageMoveLater: "Move image later",
    imageRemove: "Remove image",
    imageTooMany: "You can upload a maximum of 8 images.",
    imageInvalidType: "Please use JPG, PNG or WebP images only.",
    imageTooLarge: "One image is larger than 6 MB.",
    imageUploadFailed:
      "The images could not be uploaded. Please try again or submit the job without images.",
    timelines: [
      { value: "asap", label: "As soon as possible" },
      { value: "1-3m", label: "In 1 to 3 months" },
      { value: "3-6m", label: "In 3 to 6 months" },
      { value: "flex", label: "Flexible" },
    ],
    budgets: [
      { value: "<5k", label: "Up to CHF 5,000" },
      { value: "5-20k", label: "CHF 5,000 to 20,000" },
      { value: "20-50k", label: "CHF 20,000 to 50,000" },
      { value: ">50k", label: "Over CHF 50,000" },
      { value: "unknown", label: "Not yet decided" },
    ],
    step3Title: "Where should the work be carried out?",
    step3Text: "We connect you with contractors in your region.",
    postcode: "Postcode",
    city: "Location",
    object: "Property",
    objectPlaceholder: "Select property type",
    objects: [
      { value: "efh", label: "Detached house" },
      { value: "mfh", label: "Apartment building" },
      { value: "whg", label: "Apartment" },
      { value: "gewerbe", label: "Commercial property" },
      { value: "other", label: "Other" },
    ],
    step4Title: "How should contractors contact you?",
    step4Text:
      "Your job becomes public after our review. Verified companies with Pro access have 72 hours to express interest. You then have 7 days to select up to 5 companies. Only those companies may view your contact details and contact you about this job. Sharing contact details does not award the job.",
    firstName: "First name",
    lastName: "Last name",
    email: "Email",
    phone: "Phone (optional)",
    agreePrefix: "I accept the",
    terms: "terms",
    agreeMiddle: "and the",
    privacy: "privacy policy",
    successTitle: "Job submitted for review!",
    successIntro: "Thank you",
    successTextBeforeTitle: "Your job",
    successTextAfterTitle: "for",
    successTextEnd:
      "has been saved and will now be reviewed. You can check its status at any time in your Begner account.",
    successEmailMissing:
      "The job has been saved, but the confirmation email could not be sent. Please contact info@begner.ch and provide your reference.",
    discover: "Discover contractors",
    home: "Back to home",
  },
} as const;

const jobEmailCopy = {
  de: {
    headline: "Neue Auftragsanfrage",
    project: "Projekt",
    location: "Standort",
    contact: "Kontakt",
    category: "Kategorie",
    title: "Titel",
    description: "Beschreibung",
    timeline: "Zeitrahmen",
    budget: "Budget",
    images: "Bilder",
    postalCode: "PLZ",
    city: "Ort",
    object: "Objekt",
    name: "Name",
    email: "E-Mail",
    phone: "Telefon",
    delayed:
      "Der Auftrag ist sicher gespeichert. Die Bestätigungs-E-Mail konnte gerade nicht versendet werden; wir prüfen Ihre Anfrage trotzdem.",
  },
  fr: {
    headline: "Nouveau mandat",
    project: "Projet",
    location: "Lieu",
    contact: "Contact",
    category: "Catégorie",
    title: "Titre",
    description: "Description",
    timeline: "Calendrier",
    budget: "Budget",
    images: "Photos",
    postalCode: "NPA",
    city: "Localité",
    object: "Objet",
    name: "Nom",
    email: "E-mail",
    phone: "Téléphone",
    delayed:
      "Le mandat est bien enregistré. L’e-mail de confirmation n’a pas pu être envoyé pour le moment; votre demande sera tout de même vérifiée.",
  },
  it: {
    headline: "Nuovo incarico",
    project: "Progetto",
    location: "Luogo",
    contact: "Contatto",
    category: "Categoria",
    title: "Titolo",
    description: "Descrizione",
    timeline: "Tempistica",
    budget: "Budget",
    images: "Immagini",
    postalCode: "NPA",
    city: "Luogo",
    object: "Oggetto",
    name: "Nome",
    email: "E-mail",
    phone: "Telefono",
    delayed:
      "L’incarico è stato salvato. Al momento non è stato possibile inviare l’e-mail di conferma; verificheremo comunque la sua richiesta.",
  },
  en: {
    headline: "New job request",
    project: "Project",
    location: "Location",
    contact: "Contact",
    category: "Category",
    title: "Title",
    description: "Description",
    timeline: "Timeline",
    budget: "Budget",
    images: "Images",
    postalCode: "Postcode",
    city: "Location",
    object: "Property",
    name: "Name",
    email: "Email",
    phone: "Phone",
    delayed:
      "The job has been saved securely. The confirmation email could not be sent right now; we will still review your request.",
  },
} as const;

type JobCopy = (typeof jobCopy)[keyof typeof jobCopy];

type FormData = {
  category: string;
  title: string;
  description: string;
  timeline: string;
  budget: string;
  postcode: string;
  city: string;
  objectType: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  website: string;
  agree: boolean;
};

function AuftragPage() {
  const { language } = useLanguage();
  const authStatus = Route.useLoaderData();
  const copy = jobCopy[language];
  const mailCopy = jobEmailCopy[language];
  const steps = copy.steps.map((label, index) => ({
    id: index + 1,
    label,
    icon: stepIcons[index],
  }));
  const benefits = copy.benefits.map((text, index) => ({
    icon: [ShieldCheck, Clock, Gift][index],
    text,
  }));
  const [step, setStep] = useState(1);
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);
  const [reference, setReference] = useState("");
  const [confirmationSent, setConfirmationSent] = useState(true);
  const send = useServerFn(submitForm);
  const uploadImages = useServerFn(uploadJobImages);
  const cleanupImages = useServerFn(cleanupJobImages);
  const messages = emailMessages[language];
  const [images, setImages] = useState<SelectedImage[]>([]);
  const [titleImageId, setTitleImageId] = useState<string | null>(null);
  const [imageError, setImageError] = useState("");
  const imagesRef = useRef<SelectedImage[]>([]);
  const [data, setData] = useState<FormData>({
    category: "",
    title: "",
    description: "",
    timeline: "",
    budget: "",
    postcode: "",
    city: "",
    objectType: "",
    firstName: authStatus.authenticated ? (authStatus.profile?.firstName ?? "") : "",
    lastName: authStatus.authenticated ? (authStatus.profile?.lastName ?? "") : "",
    email: authStatus.authenticated ? authStatus.user.email : "",
    phone: authStatus.authenticated ? (authStatus.profile?.phone ?? "") : "",
    website: "",
    agree: false,
  });

  useEffect(() => {
    imagesRef.current = images;
  }, [images]);

  useEffect(
    () => () => {
      imagesRef.current.forEach((image) => URL.revokeObjectURL(image.preview));
    },
    [],
  );

  const update = <K extends keyof FormData>(k: K, v: FormData[K]) =>
    setData((d) => ({ ...d, [k]: v }));

  const addImages = (selectedFiles: FileList | File[] | null) => {
    const selected = Array.from(selectedFiles ?? []);
    if (!selected.length) return;

    const validFiles: File[] = [];
    const errors = new Set<string>();

    selected.forEach((file) => {
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        errors.add(copy.imageInvalidType);
        return;
      }
      if (file.size > MAX_IMAGE_BYTES) {
        errors.add(copy.imageTooLarge);
        return;
      }
      validFiles.push(file);
    });

    const availableSlots = Math.max(0, MAX_JOB_IMAGES - images.length);
    if (validFiles.length > availableSlots) errors.add(copy.imageTooMany);

    const additions = validFiles.slice(0, availableSlots).map((file, index) => ({
      id: `${Date.now()}-${file.lastModified}-${index}-${Math.random().toString(36).slice(2)}`,
      file,
      preview: URL.createObjectURL(file),
    }));

    if (additions.length) {
      setImages((current) => [...current, ...additions]);
      if (!titleImageId) setTitleImageId(additions[0].id);
    }

    setImageError([...errors].join(" "));
  };

  const removeImage = (id: string) => {
    const removed = images.find((image) => image.id === id);
    if (removed) URL.revokeObjectURL(removed.preview);

    const remaining = images.filter((image) => image.id !== id);
    setImages(remaining);
    setTitleImageId((current) => (current === id ? (remaining[0]?.id ?? null) : current));
    setImageError("");
  };

  const moveImage = (id: string, direction: -1 | 1) => {
    setImages((current) => {
      const index = current.findIndex((image) => image.id === id);
      const target = index + direction;
      if (
        index < 0 ||
        target < 0 ||
        target >= current.length ||
        id === titleImageId ||
        target === 0
      )
        return current;
      const reordered = [...current];
      [reordered[index], reordered[target]] = [reordered[target]!, reordered[index]!];
      return reordered;
    });
  };

  const selectTitleImage = (id: string) => {
    setImages((current) => {
      const selectedIndex = current.findIndex((image) => image.id === id);
      if (selectedIndex <= 0) return current;
      const reordered = [...current];
      const [selected] = reordered.splice(selectedIndex, 1);
      if (selected) reordered.unshift(selected);
      return reordered;
    });
    setTitleImageId(id);
  };

  if (!authStatus.authenticated || !authStatus.user.emailConfirmed || !authStatus.profile) {
    return <JobAccountGate language={language} />;
  }

  const canContinue = () => {
    if (step === 1) return data.category && data.title.trim().length > 2;
    if (step === 2)
      return (
        data.description.trim().length > 10 && data.timeline && normalizeJobBudget(data.budget)
      );
    if (step === 3)
      return /^\d{4}$/.test(data.postcode.trim()) && data.city.trim() && data.objectType;
    if (step === 4)
      return (
        data.firstName.trim() &&
        data.lastName.trim() &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email.trim()) &&
        data.agree
      );
    return false;
  };

  const next = async () => {
    if (!canContinue()) {
      toast.error(copy.validationError);
      return;
    }
    if (step < 4) {
      setStep((s) => s + 1);
      return;
    }

    setSending(true);
    try {
      let imageUrls: string[] = [];
      if (images.length) {
        const cover = images.find((image) => image.id === titleImageId);
        const orderedImages = cover
          ? [cover, ...images.filter((image) => image.id !== cover.id)]
          : images;
        const uploadedUrls: string[] = [];

        try {
          for (let offset = 0; offset < orderedImages.length; offset += JOB_UPLOAD_CONCURRENCY) {
            const batch = orderedImages.slice(offset, offset + JOB_UPLOAD_CONCURRENCY);
            const results = await Promise.allSettled(
              batch.map(async (image) => {
                const uploaded = await uploadImages({
                  data: { files: await filesToPayload([image.file]) },
                });
                const url = uploaded.urls[0];
                if (!url) throw new Error("Bild wurde ohne Speicherpfad hochgeladen.");
                return url;
              }),
            );

            for (const result of results) {
              if (result.status === "fulfilled") uploadedUrls.push(result.value);
            }
            if (results.some((result) => result.status === "rejected")) {
              throw new Error("Mindestens ein Bild konnte nicht hochgeladen werden.");
            }
          }
          imageUrls = uploadedUrls;
        } catch {
          if (uploadedUrls.length) {
            try {
              await cleanupImages({ data: { urls: uploadedUrls } });
            } catch {
              // The user-facing failure remains the upload error; cleanup is best effort.
            }
          }
          setImageError(copy.imageUploadFailed);
          setStep(2);
          toast.error(copy.imageUploadFailed);
          return;
        }
      }

      const result = await send({
        data: {
          formType: "auftrag",
          referencePrefix: "AU",
          headline: `${mailCopy.headline}: ${data.title.trim()}`,
          internalSubject: `Neue Auftragsanfrage: ${data.title}`,
          customerSubject: copy.successTitle,
          locale: language,
          customerName: `${data.firstName} ${data.lastName}`.trim(),
          customerEmail: data.email,
          website: data.website,
          termsAccepted: data.agree,
          termsVersion: "2026-10-04",
          entity: {
            category: data.category,
            title: data.title.trim(),
            description: data.description.trim(),
            timeline: data.timeline,
            budget: normalizeJobBudget(data.budget)!,
            postal_code: data.postcode.trim(),
            city: data.city.trim(),
            object_type: data.objectType,
            images: imageUrls,
            locale: language,
            contact_first_name: data.firstName.trim(),
            contact_last_name: data.lastName.trim(),
            contact_email: data.email.trim(),
            contact_phone: data.phone.trim() || null,
          },
          sections: [
            {
              title: mailCopy.project,
              fields: [
                { label: mailCopy.category, value: jobCategoryLabel(data.category, language) },
                { label: mailCopy.title, value: data.title.trim() },
                { label: mailCopy.description, value: data.description.trim() },
                { label: mailCopy.timeline, value: jobTimelineLabel(data.timeline, language) },
                { label: mailCopy.budget, value: jobBudgetLabel(data.budget, language) },
                { label: mailCopy.images, value: images.length ? String(images.length) : "" },
              ].filter((field) => field.value),
            },
            {
              title: mailCopy.location,
              fields: [
                { label: mailCopy.postalCode, value: data.postcode.trim() },
                { label: mailCopy.city, value: data.city.trim() },
                { label: mailCopy.object, value: jobObjectLabel(data.objectType, language) },
              ].filter((field) => field.value),
            },
            {
              title: mailCopy.contact,
              fields: [
                { label: mailCopy.name, value: `${data.firstName} ${data.lastName}`.trim() },
                { label: mailCopy.email, value: data.email.trim() },
                { label: mailCopy.phone, value: data.phone.trim() },
              ].filter((field) => field.value),
            },
          ],
        },
      });
      setReference(result.reference);
      setConfirmationSent(result.confirmationSent);
      setDone(true);
      if (result.confirmationSent) {
        toast.success(copy.successToast, { description: messages.confirmation });
      } else {
        toast.warning(copy.successToast, { description: mailCopy.delayed });
      }
    } catch {
      toast.error(messages.error);
    } finally {
      setSending(false);
    }
  };

  if (done)
    return <SuccessView data={data} reference={reference} confirmationSent={confirmationSent} />;

  return (
    <>
      <section className="border-b border-border bg-surface">
        <div className="container-page py-8 md:py-14">
          <SectionHeading
            eyebrow={copy.eyebrow}
            title={copy.title}
            description={copy.intro}
            level="page"
            className="max-w-2xl"
          />
          <p className="mt-3 text-sm text-muted-foreground">{copy.requiredHint}</p>
        </div>
      </section>

      <section className="bg-background py-8 md:py-12">
        <div className="container-page grid gap-8 lg:grid-cols-[1fr_320px]">
          <div className="rounded-2xl border border-border bg-card p-4 shadow-card sm:p-6 md:p-10">
            {/* Stepper */}
            <ol className="grid grid-cols-4 gap-2">
              {steps.map((s) => {
                const Icon = s.icon;
                const active = s.id === step;
                const completed = s.id < step;
                return (
                  <li
                    key={s.id}
                    className="flex flex-col items-center text-center"
                    aria-current={active ? "step" : undefined}
                  >
                    <div
                      className={`flex h-10 w-10 items-center justify-center rounded-full border-2 text-sm font-bold transition ${
                        active
                          ? "border-brand bg-brand text-primary-foreground shadow-brand"
                          : completed
                            ? "border-brand bg-brand-soft text-brand-deep"
                            : "border-border bg-background text-muted-foreground"
                      }`}
                    >
                      {completed ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                    </div>
                    <span
                      className={`mt-2 hidden text-xs font-semibold sm:block ${
                        active || completed ? "text-foreground" : "text-muted-foreground"
                      }`}
                    >
                      {s.label}
                    </span>
                  </li>
                );
              })}
            </ol>

            <div className="mt-4 h-1 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full bg-gradient-brand transition-all duration-500"
                style={{ width: `${(step / 4) * 100}%` }}
              />
            </div>

            {/* Step content */}
            <div className="mt-10">
              {step === 1 && (
                <Step1
                  copy={copy}
                  data={data}
                  onCategory={(v) => update("category", v)}
                  onTitle={(v) => update("title", v)}
                />
              )}
              {step === 2 && (
                <Step2
                  copy={copy}
                  data={data}
                  images={images}
                  titleImageId={titleImageId}
                  imageError={imageError}
                  onDescription={(v) => update("description", v)}
                  onTimeline={(v) => update("timeline", v)}
                  onBudget={(v) => update("budget", v)}
                  onAddImages={addImages}
                  onMoveImage={moveImage}
                  onRemoveImage={removeImage}
                  onTitleImage={selectTitleImage}
                />
              )}
              {step === 3 && (
                <Step3
                  copy={copy}
                  data={data}
                  onPostcode={(v) => update("postcode", v)}
                  onCity={(v) => update("city", v)}
                  onObjectType={(v) => update("objectType", v)}
                />
              )}
              {step === 4 && (
                <Step4
                  copy={copy}
                  data={data}
                  accountEmail={authStatus.user.email}
                  onChange={(k, v) => update(k, v as never)}
                />
              )}
            </div>

            {/* Navigation */}
            <div className="mt-10 grid grid-cols-2 items-center gap-3 border-t border-border pt-6">
              <Button
                variant="ghost"
                onClick={() => setStep((s) => Math.max(1, s - 1))}
                disabled={step === 1}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                {copy.back}
              </Button>
              <Button
                onClick={next}
                size="lg"
                disabled={sending}
                className="w-full font-semibold shadow-brand"
              >
                {sending ? messages.sending : step === 4 ? copy.submit : copy.next}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Side panel */}
          <aside className="space-y-4">
            <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
              <h2 className="font-display text-lg font-bold">{copy.sideTitle}</h2>
              <ul className="mt-4 space-y-3 text-sm">
                {benefits.map((b) => {
                  const Icon = b.icon;
                  return (
                    <li key={b.text} className="flex items-start gap-3">
                      <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-brand-soft text-brand-deep">
                        <Icon className="h-4 w-4" />
                      </div>
                      <span className="text-foreground">{b.text}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
            <div className="rounded-2xl bg-gradient-brand p-6 text-primary-foreground shadow-brand">
              <p className="text-sm font-semibold uppercase tracking-wider opacity-80">
                {copy.tipTitle}
              </p>
              <p className="mt-2 text-sm opacity-95">{copy.tipText}</p>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}

function Step1({
  copy,
  data,
  onCategory,
  onTitle,
}: {
  copy: JobCopy;
  data: FormData;
  onCategory: (v: string) => void;
  onTitle: (v: string) => void;
}) {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="font-display text-2xl font-bold">{copy.step1Title} *</h2>
        <p className="mt-2 text-muted-foreground">{copy.step1Text}</p>
        <div
          className="mt-6 grid grid-cols-1 gap-3 min-[380px]:grid-cols-2 sm:grid-cols-3"
          role="radiogroup"
          aria-label={copy.step1Title}
        >
          {copy.categories.map((c) => {
            const active = data.category === c.id;
            return (
              <button
                key={c.id}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => onCategory(c.id)}
                className={`flex min-h-16 items-center rounded-xl border-2 p-4 text-left text-sm font-semibold leading-snug transition ${
                  active
                    ? "border-brand bg-brand-soft text-brand-deep shadow-brand"
                    : "border-border bg-card hover:border-brand"
                }`}
              >
                {c.label}
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <Label htmlFor="title" className="text-sm font-semibold">
          {copy.titleLabel} <RequiredMark />
        </Label>
        <Input
          id="title"
          required
          value={data.title}
          onChange={(e) => onTitle(e.target.value)}
          placeholder={copy.titlePlaceholder}
          maxLength={120}
          className="mt-2 h-12 rounded-xl"
        />
      </div>
    </div>
  );
}

function Step2({
  copy,
  data,
  images,
  titleImageId,
  imageError,
  onDescription,
  onTimeline,
  onBudget,
  onAddImages,
  onMoveImage,
  onRemoveImage,
  onTitleImage,
}: {
  copy: JobCopy;
  data: FormData;
  images: SelectedImage[];
  titleImageId: string | null;
  imageError: string;
  onDescription: (v: string) => void;
  onTimeline: (v: string) => void;
  onBudget: (v: string) => void;
  onAddImages: (files: FileList | File[] | null) => void;
  onMoveImage: (id: string, direction: -1 | 1) => void;
  onRemoveImage: (id: string) => void;
  onTitleImage: (id: string) => void;
}) {
  const { language } = useLanguage();
  return (
    <div className="space-y-8">
      <div>
        <h2 className="font-display text-2xl font-bold">{copy.step2Title}</h2>
        <Label htmlFor="desc" className="mt-4 block text-sm font-semibold">
          {copy.descriptionLabel} <RequiredMark />
        </Label>
        <Textarea
          id="desc"
          required
          value={data.description}
          onChange={(e) => onDescription(e.target.value)}
          placeholder={copy.descriptionPlaceholder}
          rows={6}
          minLength={FORM_TEXT_MIN_LENGTH}
          maxLength={FORM_TEXT_MAX_LENGTH}
          className="mt-2 rounded-xl"
        />
        <FormTextCounter length={data.description.length} language={language} />
        <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
          {copy.publicContentHint}
        </p>
      </div>
      <JobImageUpload
        copy={copy}
        images={images}
        titleImageId={titleImageId}
        error={imageError}
        onAdd={onAddImages}
        onMove={onMoveImage}
        onRemove={onRemoveImage}
        onTitle={onTitleImage}
      />
      <div className="grid gap-6 md:grid-cols-2">
        <div>
          <Label className="text-sm font-semibold">
            {copy.timelineLabel} <RequiredMark />
          </Label>
          <RadioGroup
            value={data.timeline}
            onValueChange={onTimeline}
            className="mt-3 space-y-2"
            aria-label={copy.timelineLabel}
            aria-required="true"
          >
            {copy.timelines.map((t) => (
              <Label
                key={t.value}
                htmlFor={`tl-${t.value}`}
                className="flex cursor-pointer items-center gap-3 rounded-xl border border-border bg-card p-3 transition hover:border-brand"
              >
                <RadioGroupItem id={`tl-${t.value}`} value={t.value} />
                <span className="text-sm font-medium">{t.label}</span>
              </Label>
            ))}
          </RadioGroup>
        </div>
        <div>
          <Label className="text-sm font-semibold">
            {copy.budgetLabel} <RequiredMark />
          </Label>
          <RadioGroup
            value={isCustomJobBudget(data.budget) ? "custom" : data.budget}
            onValueChange={(value) => onBudget(value === "custom" ? "CHF " : value)}
            className="mt-3 space-y-2"
            aria-label={copy.budgetLabel}
            aria-required="true"
          >
            {copy.budgets.map((b) => (
              <Label
                key={b.value}
                htmlFor={`bg-${b.value}`}
                className="flex cursor-pointer items-center gap-3 rounded-xl border border-border bg-card p-3 transition hover:border-brand"
              >
                <RadioGroupItem id={`bg-${b.value}`} value={b.value} />
                <span className="text-sm font-medium">{b.label}</span>
              </Label>
            ))}
            <div className="rounded-xl border border-border bg-card p-3 transition hover:border-brand">
              <Label htmlFor="bg-custom" className="flex cursor-pointer items-center gap-3">
                <RadioGroupItem id="bg-custom" value="custom" />
                <span className="text-sm font-medium">{copy.customBudget}</span>
              </Label>
              {isCustomJobBudget(data.budget) && (
                <div className="mt-3 space-y-2 pl-7">
                  <Label htmlFor="budget-amount" className="text-sm">
                    {copy.customBudgetAmount} <RequiredMark />
                  </Label>
                  <div className="relative">
                    <Input
                      id="budget-amount"
                      type="text"
                      inputMode="decimal"
                      required
                      maxLength={20}
                      value={data.budget.slice(4)}
                      onChange={(event) => onBudget(`CHF ${event.target.value}`)}
                      placeholder="2500"
                      className="rounded-xl pr-14"
                      aria-describedby="budget-amount-hint"
                      aria-invalid={data.budget !== "CHF " && !normalizeJobBudget(data.budget)}
                    />
                    <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-muted-foreground">
                      CHF
                    </span>
                  </div>
                  <p id="budget-amount-hint" className="text-xs text-muted-foreground">
                    {copy.customBudgetHint}
                  </p>
                </div>
              )}
            </div>
          </RadioGroup>
        </div>
      </div>
    </div>
  );
}

function JobImageUpload({
  copy,
  images,
  titleImageId,
  error,
  onAdd,
  onMove,
  onRemove,
  onTitle,
}: {
  copy: JobCopy;
  images: SelectedImage[];
  titleImageId: string | null;
  error: string;
  onAdd: (files: FileList | File[] | null) => void;
  onMove: (id: string, direction: -1 | 1) => void;
  onRemove: (id: string) => void;
  onTitle: (id: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const limitReached = images.length >= MAX_JOB_IMAGES;

  return (
    <section className="overflow-hidden rounded-2xl border border-border/80 bg-surface/70">
      <div className="flex flex-col gap-3 border-b border-border/70 p-5 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-brand-soft text-brand-deep">
            <ImagePlus className="h-5 w-5" />
          </span>
          <div>
            <h3 className="font-display text-lg font-bold">{copy.imageTitle}</h3>
            <p className="mt-1 max-w-xl text-sm leading-relaxed text-muted-foreground">
              {copy.imageText}
            </p>
          </div>
        </div>
        <span className="shrink-0 rounded-full bg-background px-3 py-1.5 text-xs font-semibold text-muted-foreground shadow-sm">
          {images.length}/{MAX_JOB_IMAGES} {copy.imageCount}
        </span>
      </div>

      <div className="p-4 sm:p-5">
        <div
          onDragOver={(event) => {
            event.preventDefault();
            event.dataTransfer.dropEffect = "copy";
          }}
          onDrop={(event) => {
            event.preventDefault();
            onAdd(Array.from(event.dataTransfer.files));
          }}
          className={`rounded-2xl border-2 border-dashed px-5 py-7 text-center transition sm:px-8 ${
            limitReached
              ? "border-border bg-muted/50"
              : "border-brand/35 bg-background hover:border-brand/70 hover:bg-brand-soft/25"
          }`}
        >
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            className="sr-only"
            tabIndex={-1}
            onChange={(event) => {
              onAdd(event.currentTarget.files);
              event.currentTarget.value = "";
            }}
          />
          <p className="text-sm font-semibold text-foreground">{copy.imageDrop}</p>
          <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
            {copy.imageOptional}
          </p>
          <Button
            type="button"
            variant="outline"
            className="mt-4 h-10 rounded-xl border-brand/40 bg-background font-semibold text-brand-deep"
            disabled={limitReached}
            onClick={() => inputRef.current?.click()}
          >
            <ImagePlus className="h-4 w-4" />
            {copy.imageAdd}
          </Button>
        </div>

        {error && (
          <p
            className="mt-3 rounded-xl border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm font-medium text-destructive"
            role="alert"
          >
            {error}
          </p>
        )}

        {images.length > 0 && (
          <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
            {images.map((image, index) => {
              const isCover = image.id === titleImageId;
              return (
                <figure
                  key={image.id}
                  className={`group relative aspect-[4/3] overflow-hidden rounded-xl border bg-muted shadow-sm transition ${
                    isCover ? "border-brand ring-2 ring-brand/25" : "border-border"
                  }`}
                >
                  <img
                    src={image.preview}
                    alt={image.file.name}
                    className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-transparent to-black/25" />

                  {isCover ? (
                    <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-background/95 px-2 py-1 text-[10px] font-bold text-brand-deep shadow-sm backdrop-blur">
                      <Star className="h-3 w-3 fill-current" />
                      {copy.imageCover}
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onTitle(image.id)}
                      className="absolute left-2 top-2 grid h-11 w-11 place-items-center rounded-full bg-background/90 text-foreground shadow-sm backdrop-blur transition hover:bg-background hover:text-brand-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                      aria-label={copy.imageMakeCover}
                      title={copy.imageMakeCover}
                    >
                      <Star className="h-4 w-4" />
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={() => onRemove(image.id)}
                    className="absolute right-2 top-2 grid h-11 w-11 place-items-center rounded-full bg-background/90 text-destructive shadow-sm backdrop-blur transition hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                    aria-label={copy.imageRemove}
                    title={copy.imageRemove}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>

                  <span className="absolute bottom-2 left-2 grid h-7 min-w-7 place-items-center rounded-full bg-black/50 px-2 text-xs font-bold text-white backdrop-blur">
                    {index + 1}
                  </span>
                  <div className="absolute bottom-2 right-2 flex gap-1">
                    <button
                      type="button"
                      disabled={index <= 1}
                      onClick={() => onMove(image.id, -1)}
                      className="grid h-11 w-11 place-items-center rounded-full bg-background/90 text-foreground shadow-sm backdrop-blur transition hover:bg-background disabled:opacity-35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                      aria-label={copy.imageMoveEarlier}
                      title={copy.imageMoveEarlier}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      disabled={isCover || index === images.length - 1}
                      onClick={() => onMove(image.id, 1)}
                      className="grid h-11 w-11 place-items-center rounded-full bg-background/90 text-foreground shadow-sm backdrop-blur transition hover:bg-background disabled:opacity-35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                      aria-label={copy.imageMoveLater}
                      title={copy.imageMoveLater}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </figure>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

function Step3({
  copy,
  data,
  onPostcode,
  onCity,
  onObjectType,
}: {
  copy: JobCopy;
  data: FormData;
  onPostcode: (v: string) => void;
  onCity: (v: string) => void;
  onObjectType: (v: string) => void;
}) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-2xl font-bold">{copy.step3Title}</h2>
        <p className="mt-2 text-muted-foreground">{copy.step3Text}</p>
      </div>
      <div className="grid gap-4 md:grid-cols-[200px_1fr]">
        <div>
          <Label htmlFor="plz" className="text-sm font-semibold">
            {copy.postcode} <RequiredMark />
          </Label>
          <Input
            id="plz"
            required
            value={data.postcode}
            onChange={(e) => onPostcode(e.target.value)}
            placeholder="8001"
            autoComplete="postal-code"
            inputMode="numeric"
            pattern="[0-9]{4}"
            maxLength={4}
            className="mt-2 h-12 rounded-xl"
          />
        </div>
        <div>
          <Label htmlFor="city" className="text-sm font-semibold">
            {copy.city} <RequiredMark />
          </Label>
          <Input
            id="city"
            required
            value={data.city}
            onChange={(e) => onCity(e.target.value)}
            placeholder="Zürich"
            autoComplete="address-level2"
            maxLength={100}
            className="mt-2 h-12 rounded-xl"
          />
        </div>
      </div>
      <div>
        <Label className="text-sm font-semibold">
          {copy.object} <RequiredMark />
        </Label>
        <Select value={data.objectType} onValueChange={onObjectType}>
          <SelectTrigger
            className="mt-2 h-12 rounded-xl"
            aria-label={copy.object}
            aria-required="true"
          >
            <SelectValue placeholder={copy.objectPlaceholder} />
          </SelectTrigger>
          <SelectContent>
            {copy.objects.map((item) => (
              <SelectItem key={item.value} value={item.value}>
                {item.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function Step4({
  copy,
  data,
  accountEmail,
  onChange,
}: {
  copy: JobCopy;
  data: FormData;
  accountEmail: string;
  onChange: (k: keyof FormData, v: string | boolean) => void;
}) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-2xl font-bold">{copy.step4Title}</h2>
        <div className="mt-4 flex items-start gap-3 rounded-2xl border border-brand/20 bg-brand-soft/45 p-4">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-background text-brand-deep shadow-sm">
            <ShieldCheck className="h-5 w-5" />
          </span>
          <p className="text-sm leading-relaxed text-foreground/80">{copy.step4Text}</p>
        </div>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <Label htmlFor="fn" className="text-sm font-semibold">
            {copy.firstName} <RequiredMark />
          </Label>
          <Input
            id="fn"
            required
            value={data.firstName}
            onChange={(e) => onChange("firstName", e.target.value)}
            autoComplete="given-name"
            maxLength={55}
            className="mt-2 h-12 rounded-xl"
          />
        </div>
        <div>
          <Label htmlFor="ln" className="text-sm font-semibold">
            {copy.lastName} <RequiredMark />
          </Label>
          <Input
            id="ln"
            required
            value={data.lastName}
            onChange={(e) => onChange("lastName", e.target.value)}
            autoComplete="family-name"
            maxLength={55}
            className="mt-2 h-12 rounded-xl"
          />
        </div>
        <div>
          <Label htmlFor="email" className="text-sm font-semibold">
            {copy.email} <RequiredMark />
          </Label>
          <Input
            id="email"
            type="email"
            required
            value={data.email}
            onChange={(e) => onChange("email", e.target.value)}
            readOnly
            autoComplete="email"
            maxLength={255}
            className="mt-2 h-12 rounded-xl"
          />
          <p className="mt-1.5 text-xs text-muted-foreground">{accountEmail}</p>
        </div>
        <div>
          <Label htmlFor="phone" className="text-sm font-semibold">
            {copy.phone}
          </Label>
          <Input
            id="phone"
            type="tel"
            value={data.phone}
            onChange={(e) => onChange("phone", e.target.value)}
            autoComplete="tel"
            maxLength={40}
            className="mt-2 h-12 rounded-xl"
          />
        </div>
      </div>
      <Label
        htmlFor="agree"
        className="flex cursor-pointer items-start gap-3 rounded-xl border border-border bg-card p-4 text-sm"
      >
        <Checkbox
          id="agree"
          aria-required="true"
          checked={data.agree}
          onCheckedChange={(v) => onChange("agree", Boolean(v))}
          className="mt-0.5"
        />
        <span className="text-muted-foreground">
          <RequiredMark /> {copy.agreePrefix}{" "}
          <Link
            to="/agb"
            className="font-semibold text-brand-deep underline-offset-4 hover:underline"
          >
            {copy.terms}
          </Link>{" "}
          {copy.agreeMiddle}{" "}
          <Link
            to="/datenschutz"
            className="font-semibold text-brand-deep underline-offset-4 hover:underline"
          >
            {copy.privacy}
          </Link>
          {"."}
        </span>
      </Label>
      <div className="absolute -left-[10000px] top-auto h-px w-px overflow-hidden" aria-hidden>
        <Label htmlFor="website">Website</Label>
        <Input
          id="website"
          name="website"
          value={data.website}
          onChange={(event) => onChange("website", event.target.value)}
          autoComplete="off"
          tabIndex={-1}
        />
      </div>
    </div>
  );
}

function RequiredMark() {
  return (
    <span className="text-destructive" aria-hidden="true">
      *
    </span>
  );
}

function JobAccountGate({ language }: { language: keyof typeof accountGateCopy }) {
  const copy = accountGateCopy[language];
  return (
    <main className="relative grid min-h-[68vh] place-items-center overflow-hidden bg-muted/25 px-6 py-20">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-80 bg-brand-soft/70 blur-3xl" />
      <div className="relative w-full max-w-2xl rounded-3xl border border-border/70 bg-card/95 p-8 text-center shadow-card sm:p-12">
        <span className="mx-auto grid size-16 place-items-center rounded-2xl bg-brand-soft text-primary">
          <User className="size-7" />
        </span>
        <p className="mt-6 text-xs font-bold uppercase tracking-[0.16em] text-primary">
          {copy.eyebrow}
        </p>
        <h1 className="mt-2 font-display text-3xl font-bold tracking-tight sm:text-4xl">
          {copy.title}
        </h1>
        <p className="mx-auto mt-4 max-w-xl leading-7 text-muted-foreground">{copy.text}</p>
        <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
          <Button asChild size="lg" variant="outline">
            <Link
              to="/login"
              search={{ mode: "anmelden", next: "/auftrag-einstellen", typ: "kunde" }}
            >
              {copy.login}
            </Link>
          </Button>
          <Button asChild size="lg" className="shadow-brand">
            <Link
              to="/login"
              search={{ mode: "registrieren", next: "/auftrag-einstellen", typ: "kunde" }}
            >
              {copy.register}
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </main>
  );
}

function SuccessView({
  data,
  reference,
  confirmationSent,
}: {
  data: FormData;
  reference: string;
  confirmationSent: boolean;
}) {
  const { language } = useLanguage();
  const copy = jobCopy[language];

  return (
    <section className="bg-surface py-20">
      <div className="container-page max-w-2xl">
        <div className="rounded-3xl border border-border bg-card p-10 text-center shadow-card">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-brand-soft text-brand-deep">
            <CheckCircle2 className="h-8 w-8" />
          </div>
          <h1 className="mt-6 font-display text-3xl font-bold md:text-4xl">{copy.successTitle}</h1>
          {confirmationSent ? (
            <p className="mt-3 text-muted-foreground">
              {copy.successIntro}, {data.firstName}. {copy.successTextBeforeTitle} "
              <span className="font-semibold text-foreground">{data.title}</span>"{" "}
              {copy.successTextAfterTitle} {data.city} {copy.successTextEnd}
            </p>
          ) : (
            <p className="mt-3 text-muted-foreground">{copy.successEmailMissing}</p>
          )}
          {reference && <p className="mt-3 text-sm font-semibold text-brand-deep">{reference}</p>}
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" className="font-semibold shadow-brand">
              <Link to="/auftragnehmer">
                {copy.discover}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/konto">{accountGateCopy[language].eyebrow}</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
